// 「今日の宇宙」詳細ページ：五つのテーマの今日の情報を一覧で見せる（無料）
import React from 'react';
import { useUserLocation } from '../../lib/userLocation';
import RegionPrompt from '../../components/RegionPrompt';
import { useCosmosData } from './useCosmosData';
import { buildThemes, pickDailyTheme, pendingThemes, THEME_ORDER, ThemeKey } from './themes';
import { dailyPractice } from './practice';
import OneMinute from './OneMinute';

const PLACEHOLDER: Record<ThemeKey, { label: string; icon: string; text: string }> = {
  moon: { label: '月のリズム', icon: '🌙', text: '' },
  sun: { label: '太陽のリズム', icon: '☀️', text: '' },
  spaceWeather: { label: '宇宙天気', icon: '🌞', text: '宇宙天気を取得しています…' },
  nightSky: { label: '夜空', icon: '✨', text: '今夜の空を確かめています…' },
  earth: { label: '地球のリズム', icon: '🌏', text: '' },
};

export default function CosmosPage({ profile, onBack, onUpdateLocation }: {
  profile: any;
  onBack: () => void;
  onUpdateLocation?: (id: string, location: string) => any;
}) {
  const loc = useUserLocation(profile?.location);
  const data = useCosmosData(loc);
  const themes = buildThemes(data);
  const todays = pickDailyTheme(themes, data.now, pendingThemes(data));
  const byKey = new Map(themes.map((t) => [t.key, t]));
  const d = data.now;
  const showPrompt = profile && onUpdateLocation && (!String(profile.location || '').trim() || loc === null);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '14px', animation: 'fadeIn 0.3s' }}>
      <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#92400e', fontSize: '15px', cursor: 'pointer', padding: '4px 0', marginBottom: '8px' }}>
        ‹ 今日の運気へ戻る
      </button>

      <div style={{ background: 'linear-gradient(160deg,#0b1026,#1e1b4b 60%,#0f172a)', borderRadius: '16px', border: '1px solid #C9A84C', padding: '18px 16px', color: '#f8fafc' }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#e8c96a', letterSpacing: '2px', fontFamily: "'Shippori Mincho', serif" }}>
          🌌 今日の宇宙
        </div>
        <div style={{ fontSize: '13px', color: '#cbd5e1', marginTop: '4px' }}>
          {d.getFullYear()}年{d.getMonth() + 1}月{d.getDate()}日{loc ? `・${loc.label}` : ''}
        </div>

        {showPrompt && (
          <div style={{ marginTop: '14px' }}>
            <RegionPrompt name={profile.name} current={loc === null ? String(profile.location || '').trim() : ''} onSave={(v) => onUpdateLocation!(profile.id, v)} />
          </div>
        )}
        {!profile && loc === null && (
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#cbd5e1', lineHeight: 1.7 }}>
            日の出・日の入りと今夜の空は、位置情報を許可するか、プロフィールに地域を登録すると表示されます。
          </div>
        )}

        {todays && (
          <div style={{ marginTop: '14px' }}>
            <OneMinute text={dailyPractice(todays.key, data.now)} />
          </div>
        )}

        {THEME_ORDER.map((key) => {
          const t = byKey.get(key);
          const ph = PLACEHOLDER[key];
          const isToday = todays?.key === key;
          if (!t && key === 'spaceWeather' && data.wx === null) return null; // 取得できなかった日は出さない
          if (!t && key === 'nightSky' && data.loc === null) return null;
          return (
            <section key={key} style={{ marginTop: '16px', paddingTop: '14px', borderTop: '0.5px solid #C9A84C55' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '26px', lineHeight: 1 }}>{t ? t.icon : ph.icon}</span>
                <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#e8c96a', fontFamily: "'Shippori Mincho', serif", letterSpacing: '1px' }}>{t ? t.label : ph.label}</span>
                {isToday && <span style={{ fontSize: '11px', color: '#0f172a', background: '#e8c96a', borderRadius: '999px', padding: '1px 8px' }}>今日のひとこと</span>}
              </div>
              {t ? (
                <>
                  <div style={{ fontSize: '15px', lineHeight: 1.9, fontFamily: "'Noto Serif JP', serif" }}>{t.headline}</div>
                  {t.lines.map((l, i) => <div key={i} style={{ fontSize: '13px', color: '#cbd5e1', marginTop: '4px', lineHeight: 1.7 }}>{l}</div>)}
                  {t.notes.map((l, i) => <div key={i} style={{ fontSize: '13px', color: '#e8c96a', marginTop: '4px', lineHeight: 1.7 }}>{l}</div>)}
                  {t.needsLocation && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>地域を登録すると、お住まいの空に合わせた情報が加わります。</div>}
                </>
              ) : (
                <div style={{ fontSize: '13px', color: '#cbd5e1' }}>{ph.text}</div>
              )}
            </section>
          );
        })}

        <div style={{ marginTop: '18px', paddingTop: '12px', borderTop: '0.5px solid #C9A84C55', fontSize: '11px', color: '#94a3b8', lineHeight: 1.7 }}>
          月・太陽・惑星・季節・潮回りは端末内の天文計算、宇宙天気は NOAA 宇宙天気予報センター、国際宇宙ステーションの軌道は CelesTrak の公開データによります。
          潮回りは月齢からの目安で、実際の潮位は地域の潮汐表でご確認ください。
          宇宙や星の動きは、健康や運勢を断定するものではありません。
        </div>
      </div>
    </div>
  );
}
